import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { ComponentProps, FC, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { differenceBy, keyBy, sortBy, uniq, uniqBy } from 'lodash';

import API, { getApiUrl } from '@api/API';

import { useTopEntertainersQuery } from '@queries/EntertainerQueries';
import {
  FeedEvent,
  useEventQuery,
  useEvents,
  useSeriesPendingEvents,
  useUnauthorizedEventsQuery,
} from '@queries/EventQueries';
import {
  Series,
  useSeries,
  useSeriesByTagSlug,
  useSeriesInstagramHandleLatestPostsQuery,
} from '@queries/TagQueries';

import {
  useCreateEventMutation,
  useMarkDuplicateEventMutation,
  useUpdateEventMutation,
} from '@mutations/EventMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { CurrentCity, useCitiesQuery } from '@hooks/useCurrentUserLocation';
import { useIsVisible } from '@hooks/useIsVisible';

import { imageUrlToBase64 } from '@utilities/imageUrlToBase64';
import { openExternalUrl } from '@utilities/openExternalUrl';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import EventCard from '@components/cards/EventCard';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { LineAwesomeIcon } from '@components/icons/LineAwesomeIcon';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import {
  PopoverActionList,
  PopoverActionListButton,
} from '@components/layout/PopoverActionList/PopoverActionList';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import {
  CreateEditEventPopup,
  SubmitEventProperties,
} from '@screens/CreateEditEventScreen/CreateEditEventForm';
import { ScheduledSocialPostSection } from '@screens/entertainer/DragKingCommunityScreen/ScheduledSocialPost';
import { MultiSelectField } from '@screens/entertainer/components/CitiesField';

import { SuperAdminManageLocalDragGuidePost } from '../components/SuperAdminManageLocalDragGuidePost';
import { CategorizeExistingEventsList } from './CategorizeExistingEventsList';
import { ExistingEventsEntertainersList } from './ExistingEventsEntertainersList';
import { ExistingEventsReferrerList } from './ExistingEventsReferrerList';
import { SeriesProducersSection } from './SeriesProducersSection';
import { SeriesVenuesSection } from './SeriesVenuesSection';

const CarouselItemEvent: FC<{ event: SubmitEventProperties }> = ({ event }) => {
  return (
    <div>
      <div>
        <strong>{event.title}</strong>
      </div>
    </div>
  );
};

const CarouselItem = ({
  username,
  postId,
  index,
  events,
  onAddEvent,
}: {
  username: string;
  postId: string;
  index: number;
  events?: any[];
  onAddEvent?: (imgUrl: string) => void;
}) => {
  const postImgUrl = `${getApiUrl()}/v1/instagram/${username}/${postId}/${index}/thumbnail.jpg`;
  const handleAddEvent = () => {
    onAddEvent(postImgUrl);
  };

  return (
    <div>
      <div>
        <img className="w-100" src={postImgUrl} />
      </div>
      <div className="d-grid gap-2">
        {events?.map((event, i) => (
          <CarouselItemEvent key={i} event={event} />
        ))}
      </div>
      <Button
        className="mt-3"
        roundness="rounded"
        color="neutral"
        variant="smooth"
        startIcon={<FontAwesomeIcon name="plus" />}
        onClick={handleAddEvent}
      >
        Add Event
      </Button>
    </div>
  );
};

const ExistingEventCard: FC<{
  event: FeedEvent;
  tagId: string;
  onEdit: () => void;
  onEditRecurringParentEvent: (recurringParentEventId: string) => void;
}> = ({ event, tagId, onEdit, onEditRecurringParentEvent }) => {
  const [isDeleted, setIsDeleted] = useState(false);
  const [isInSeries, setIsInSeries] = useState(
    (event as any).series?.some(({ _id }) => _id === tagId),
  );
  const { openConfirmDialog } = useDialog();
  const { updateEventAsync } = useUpdateEventMutation({ eventId: event._id });

  const handleAddToSeries = (isAdd: boolean) => {
    openConfirmDialog({
      title: isAdd ? 'Add Event to Series?' : 'Remove Event from Series?',
      text: isAdd
        ? 'Are you sure you want to add this existing event to the series?'
        : 'Are you sure you want to remove this existing event from the series?',
      onConfirm: async () => {
        try {
          const { event: fetchedEvent } = await useEventQuery.fn(event._id);
          let tagIds = fetchedEvent.series?.map(({ _id }) => _id).filter(Boolean) ?? [];

          if (isAdd) {
            tagIds.push(tagId);
          } else {
            tagIds = tagIds.filter((id) => id !== tagId);
          }

          await updateEventAsync({
            tagIds: uniq(tagIds),
          });

          setIsInSeries(isAdd);
        } catch (e) {
          Sentry.captureException(e);
          alert('Failed to update event in series');
        }
      },
      buttonOk: 'Yes',
    });
  };

  const handleDelete = () => {
    openConfirmDialog({
      title: 'Delete Event?',
      text: 'Are you sure you want to delete this event? This action cannot be undone.',
      onConfirm: async () => {
        await updateEventAsync({ isActive: false });
        setIsDeleted(true);
      },
      buttonOk: 'Yes',
    });
  };

  const buttons = useMemo<PopoverActionListButton[]>(() => {
    const actionButtons: PopoverActionListButton[] = [];

    if (event.isRecurring) {
      actionButtons.push({
        onClick: async () => {
          const { event: hydratedEvent } = await useEventQuery.fn(event._id);
          onEditRecurringParentEvent(hydratedEvent.recurringParentEventId);
        },
        text: 'Edit Parent Event',
      });
    }

    return actionButtons;
  }, [event.isRecurring]);

  return (
    <div className="w-100 h-100 d-flex flex-column">
      <EventCard className="w-100" event={event} onClick={() => handleAddToSeries(true)} />
      <div className="mt-2">
        {isDeleted ? (
          <Button color="danger">Deleted!</Button>
        ) : (
          <div className="d-flex gap-2 align-items-center">
            <div className="w-100">
              <Button onClick={onEdit}>Edit</Button>
            </div>
            {!!buttons.length && (
              <div>
                <PopoverActionList buttons={buttons} />
              </div>
            )}
            <div>
              <IconButton
                variant="smooth"
                color={isInSeries ? 'teal' : 'neutral'}
                onClick={() => handleAddToSeries(!isInSeries)}
              >
                <LineAwesomeIcon name="check" />
              </IconButton>
            </div>
            <div>
              <IconButton variant="smooth" color="danger" onClick={handleDelete}>
                <LineAwesomeIcon name="trash" />
              </IconButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MergeButton: FC<{
  primaryEvent: FeedEvent;
  duplicateEventId: string;
  onConfirm: (primaryEvent: FeedEvent) => void;
}> = ({ primaryEvent, duplicateEventId, onConfirm }) => {
  const { openConfirmDialog } = useDialog();
  const { markDuplicateEventAsync } = useMarkDuplicateEventMutation({ eventId: primaryEvent._id });

  return (
    <Button
      variant="outlined"
      color="neutral"
      size="sm"
      onClick={() => {
        openConfirmDialog({
          title: 'Merge Events?',
          text: `Are you sure you want to reject this event and merge it into "${primaryEvent.title}"`,
          onConfirm: () => {
            markDuplicateEventAsync({ duplicateEventId, status: 'rejected' });
            onConfirm(primaryEvent);
          },
          buttonOk: 'Yes',
        });
      }}
    >
      Merge
    </Button>
  );
};

const UpdateParentImageButton: FC<{ recurringParentEventId: string; imageUrl: string }> = ({
  recurringParentEventId,
  imageUrl,
}) => {
  const { isSubmitting, updateEventAsync } = useUpdateEventMutation({
    eventId: recurringParentEventId,
  });
  const [didUpdateParentImage, setDidUpdateParentImage] = useState(false);

  return (
    <Button
      variant="smooth"
      color="neutral"
      roundness="rounded"
      disabled={isSubmitting || didUpdateParentImage}
      onClick={async () => {
        const base64Image = await imageUrlToBase64(imageUrl);
        await updateEventAsync({ eventImage: base64Image });
        setDidUpdateParentImage(true);
      }}
    >
      {didUpdateParentImage ? 'Replaced Parent Image!' : 'Replace Parent Image'}
    </Button>
  );
};

const MergeEventDetails: FC<{
  primaryEventId: string;
  duplicateEventId: string;
  onSave: (updatedProperties: Partial<SubmitEventProperties>) => void;
}> = ({ primaryEventId, duplicateEventId, onSave }) => {
  const { event: primaryEvent } = useEventQuery(primaryEventId);
  const { event: duplicateEvent } = useEventQuery(duplicateEventId);
  const didInitRef = useRef(false);
  const [updatedEventProperties, setUpdatedEventProperties] = useState<
    Partial<SubmitEventProperties>
  >({});
  const { isSubmitting, updateEventAsync } = useUpdateEventMutation({ eventId: primaryEventId });
  const [didUpdateImage, setDidUpdateImage] = useState(false);

  const entertainerOptions = useMemo(() => {
    return uniqBy(
      [...(primaryEvent?.entertainers ?? []), ...(duplicateEvent?.entertainers ?? [])],
      '_id',
    );
  }, [primaryEvent, duplicateEvent]);

  const addedEntertainers = useMemo(() => {
    // entertainer in duplicateEvent but not in primaryEvent
    return differenceBy(
      (duplicateEvent?.entertainers ?? []) as Array<{ _id: string; title: string }>,
      (primaryEvent?.entertainers ?? []) as Array<{ _id: string; title: string }>,
      '_id',
    );
  }, [primaryEvent, duplicateEvent]);

  const addedEntertainerOptionsById = useMemo(() => {
    return keyBy(addedEntertainers, '_id');
  }, [addedEntertainers]);

  useEffect(() => {
    if (primaryEvent?._id && duplicateEvent?._id && !didInitRef.current) {
      setUpdatedEventProperties((prevValue) => ({
        ...prevValue,
        entertainerIds: entertainerOptions?.map(({ _id }) => _id) ?? [],
        entertainersByInstagram: duplicateEvent.entertainersByInstagram,
      }));

      didInitRef.current = true;
    }
  }, [primaryEvent?._id, duplicateEvent?._id]);

  if (!primaryEvent || !duplicateEvent) {
    return <Skeleton height={80} />;
  }

  return (
    <div>
      <div className="d-grid gap-2 mb-2">
        <img className="w-100" src={primaryEvent?.img} />
        <Button
          variant="smooth"
          color="neutral"
          roundness="rounded"
          disabled={isSubmitting || didUpdateImage}
          onClick={async () => {
            const base64Image = await imageUrlToBase64(duplicateEvent.img);
            await updateEventAsync({ eventImage: base64Image });
            setDidUpdateImage(true);
          }}
        >
          {didUpdateImage ? 'Replaced!' : 'Replace Original Image'}
        </Button>
        {primaryEvent.recurringParentEventId && (
          <UpdateParentImageButton
            recurringParentEventId={primaryEvent.recurringParentEventId}
            imageUrl={duplicateEvent.img}
          />
        )}
      </div>

      <div className="mb-2">
        <Input
          type="text"
          defaultValue={primaryEvent.title}
          onChange={(e) =>
            setUpdatedEventProperties((prevValue) => ({
              ...prevValue,
              title: e.target.value,
            }))
          }
        />
      </div>

      {entertainerOptions?.length || updatedEventProperties?.entertainersByInstagram?.length ? (
        <>
          {entertainerOptions?.length && (
            <>
              <MultiSelectField
                label={'Drag Performers'}
                name="entertainerIds"
                allowSearch
                defaultValue={entertainerOptions?.map(({ _id }) => _id) ?? []}
                options={entertainerOptions
                  ?.map(({ _id, title }) => ({
                    value: _id,
                    label: title ?? _id,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))}
                onChange={(updatedEntertainerIds) => {
                  setUpdatedEventProperties((prevValue) => ({
                    ...prevValue,
                    entertainerIds: updatedEntertainerIds,
                  }));
                }}
              />
              <div className="d-grid mt-1">
                {updatedEventProperties?.entertainerIds
                  ?.filter((entertainerId) => addedEntertainerOptionsById[entertainerId])
                  ?.map((entertainerId) => (
                    <div>{addedEntertainerOptionsById[entertainerId]?.title}</div>
                  ))}
              </div>
            </>
          )}

          <div className="d-grid gap-2">
            {!!updatedEventProperties?.entertainersByInstagram?.length &&
              updatedEventProperties?.entertainersByInstagram?.map((instagram, index) => (
                <Input
                  key={`${index}`}
                  type="text"
                  placeholder="eg. @dragqueen.handle"
                  defaultValue={instagram}
                  onChange={(e) =>
                    setUpdatedEventProperties((prevValue) => {
                      const entertainersByInstagram = prevValue?.entertainersByInstagram ?? [];
                      entertainersByInstagram[index] = e.target.value;

                      return {
                        ...prevValue,
                        entertainersByInstagram,
                      };
                    })
                  }
                />
              ))}
          </div>
        </>
      ) : (
        <EmptyState title="No Tagged Users" text="No users were tagged with this event" />
      )}

      <div className="mt-2">
        <Button
          variant="smooth"
          color="neutral"
          roundness="rounded"
          disabled={isSubmitting}
          onClick={async () => {
            await updateEventAsync(updatedEventProperties);
            onSave(updatedEventProperties);
          }}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export const PendingEventCard: FC<{
  isReviewed?: boolean;
  event: FeedEvent & {
    sourceInstagramUser?: { username: string; fullName: string; biography: string };
  };
  cityId: string;
  tagId: string;
  onMergeSave: (eventId: string, updatedProperties: Partial<SubmitEventProperties>) => void;
  onEdit: () => void;
  onActionCallback: () => void;
}> = ({
  isReviewed,
  event,
  tagId,
  cityId: defaultCityId,
  onEdit,
  onMergeSave,
  onActionCallback,
}) => {
  const { openConfirmDialog } = useDialog();
  const { updateEventAsync } = useUpdateEventMutation({ eventId: event._id });
  const { cities = [] } = useCitiesQuery();
  const [primaryEvent, setPrimaryEvent] = useState<FeedEvent>();
  const [cityId, setCityId] = useState<string>(defaultCityId);
  const cityOptions = useMemo(() => {
    return [
      { value: '', label: '-- Default --' },
      ...cities.map(({ _id, name }) => ({
        value: _id,
        label: name,
      })),
    ];
  }, [cities]);

  const divRef = useRef<HTMLDivElement>(null);
  const { isVisible } = useIsVisible(divRef);
  const {
    eventsAreReady,
    events: existingEvents = [],
    refetchEvents,
  } = useUnauthorizedEventsQuery(
    {
      cityId,
      startDate: event.startDate,
      endDate: event.startDate,
      rsvpData: 'ignore',
      forceSupply: 'true',
      includeUnapprovedRecurringEvents: 'true',
    },
    Boolean(tagId) && isVisible,
  );

  const handleMergeSave = (updatedProperties: Partial<SubmitEventProperties>) => {
    onMergeSave(primaryEvent._id, updatedProperties);
  };

  const instagramUserActions: ComponentProps<typeof PopoverActionList>['buttons'] = [
    {
      onClick: () => {
        openExternalUrl(`https://www.instagram.com/${event.sourceInstagramUser.username}/`);
      },
      text: 'View Instagram',
    },
    {
      onClick: () => {
        API.post(`/v1/super-admin/instagram-accounts/manage`, {
          username: event.sourceInstagramUser.username,
          action: 'skip',
          eventId: event._id,
        });
        onActionCallback();
      },
      text: 'Skip Profile',
    },
  ];

  return (
    <div ref={divRef} className="w-100 h-100 d-flex flex-column">
      <div className="mb-2 d-flex justify-content-between align-items-start">
        <div>
          {`Posted by:`}
          <br />
          {event.sourceInstagramUser?.fullName ?? 'Unknown Source'}
        </div>
        <div>
          {event.sourceInstagramUser?.fullName && (
            <PopoverActionList buttons={instagramUserActions} />
          )}
        </div>
      </div>
      <EventCard
        className="w-100"
        event={event}
        onClick={onEdit}
        hideActions
        hideRsvp
        showPromoter={false}
      />
      {!isReviewed ? (
        <>
          <div className="mt-2">
            <div className="d-flex gap-2 align-items-center">
              <div className="w-100">
                <Button onClick={onEdit}>Edit</Button>
              </div>
              <div>
                <IconButton
                  variant="smooth"
                  color="danger"
                  onClick={() => {
                    openConfirmDialog({
                      title: 'Reject Event?',
                      text: 'Are you sure you want to reject this event? This action cannot be undone.',
                      onConfirm: () => {
                        updateEventAsync({ status: 'rejected' });
                        onActionCallback();
                      },
                      buttonOk: 'Yes',
                    });
                  }}
                >
                  <LineAwesomeIcon name="trash" />
                </IconButton>
              </div>
            </div>
            {!primaryEvent && (
              <>
                {!!existingEvents.length && (
                  <div
                    style={{
                      overflowY: 'auto',
                      maxHeight: 150,
                      background: 'rgba(255,255,255,.1)',
                      borderRadius: 8,
                    }}
                    className="mt-2 p-2"
                  >
                    <div className="d-grid gap-2">
                      {existingEvents.map((sameDayEvent) => (
                        <div key={sameDayEvent._id} className="d-grid">
                          <div className="d-flex gap-2 align-items-center justify-content-between">
                            <div className="d-flex gap-2 align-items-center">
                              <div>
                                <strong>{sameDayEvent.title}</strong>
                              </div>
                              {!sameDayEvent.isApproved && <div>⚠️</div>}
                            </div>
                            <div>
                              <MergeButton
                                primaryEvent={sameDayEvent}
                                duplicateEventId={event._id}
                                onConfirm={setPrimaryEvent}
                              />
                            </div>
                          </div>
                          <span>
                            {sameDayEvent.start} — {sameDayEvent.venue?.title || 'Unknown Venue'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="d-flex gap-2  mt-1">
                  <Select
                    options={cityOptions}
                    value={cityId}
                    onChange={(e) => setCityId(e.target.value)}
                  />

                  <Button
                    roundness="rounded"
                    variant="smooth"
                    color="neutral"
                    onClick={() => refetchEvents()}
                  >
                    Refetch
                  </Button>
                </div>
              </>
            )}
            {primaryEvent && (
              <div className="mt-2">
                <MergeEventDetails
                  primaryEventId={primaryEvent?._id}
                  duplicateEventId={event._id}
                  onSave={handleMergeSave}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="mt-2">
            <Button color="green">Reviewed!</Button>
          </div>
        </>
      )}
    </div>
  );
};

const useQueueEventProcessingMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ tagId, ...payload }: { tagId: string; target?: 'entertainers' | 'producers' }) =>
      API.post(`/v1/series/${tagId}/queue-event-processing`, payload),
  });

  return {
    isQueuingEventProcessing: isPending,
    queueEventProcessingAsync: mutateAsync,
  };
};

const QueueEventProcessingButton: FC<{ tagId: string }> = ({ tagId }) => {
  const [didQueue, setDidQueue] = useState(false);
  const { isQueuingEventProcessing, queueEventProcessingAsync } = useQueueEventProcessingMutation();

  const handleClick = async () => {
    const target = tagId === '692605f9eecc7706e831fd13' ? 'producers' : 'entertainers';
    await queueEventProcessingAsync({ tagId, target });
    setDidQueue(true);
  };

  const buttonText = useMemo(() => {
    if (didQueue) {
      return 'Queued!';
    }

    if (isQueuingEventProcessing) {
      return 'Queuing...';
    }

    return 'Queue Event Processing';
  }, [didQueue, isQueuingEventProcessing]);

  return (
    <Button
      disabled={isQueuingEventProcessing || didQueue}
      roundness="rounded"
      onClick={handleClick}
    >
      {buttonText}
    </Button>
  );
};

interface EditEventPopupProps {
  cityOptions: { value: string; label: string }[];
  eventId: string;
  tagId: string;
  onApprove?: (eventId: string) => void;
  onClose: (didSave?: boolean) => void;
}

export const SeriesAdminEditEventPopup: FC<EditEventPopupProps> = ({
  cityOptions,
  eventId,
  tagId,
  onApprove,
  onClose,
}) => {
  const { isFetched: seriesIsReady, series = [] } = useSeries();
  const { event: fetchedEvent } = useEventQuery(eventId);
  const { updateEventAsync } = useUpdateEventMutation({ eventId });

  const AdditionalFields: FC<{
    updatedEventProperties: Partial<SubmitEventProperties>;
    setUpdatedEventProperties: (
      updatedEventProperties: SetStateAction<Partial<SubmitEventProperties>>,
    ) => void;
  }> = ({ updatedEventProperties, setUpdatedEventProperties }) => {
    return (
      <>
        {seriesIsReady && (
          <MultiSelectField
            allowSearch
            defaultValue={updatedEventProperties.tagIds}
            label="Series"
            options={sortBy(
              series.map(({ _id, title }) => ({
                value: _id,
                label: title,
              })),
              'label',
            )}
            onChange={(updatedSeriesIds) => {
              setUpdatedEventProperties((prevValue) => ({
                ...prevValue,
                tagIds: updatedSeriesIds,
              }));
            }}
          />
        )}
      </>
    );
  };

  const event = {
    ...fetchedEvent,
    status: fetchedEvent?.status === 'pending' ? 'approved' : fetchedEvent?.status,
    tagIds: uniq(
      [
        ...(fetchedEvent?.status === 'pending' ? [tagId] : []),
        ...(fetchedEvent?.series?.map(({ _id }) => _id) ?? []),
      ].filter(Boolean),
    ),
  };

  return (
    <CreateEditEventPopup
      isLoading={!event?._id || !seriesIsReady}
      isVisible={!!eventId}
      onClose={() => onClose()}
      event={event}
      cityOptions={cityOptions}
      AdditionalFields={AdditionalFields}
      onSubmit={async (payload) => {
        await updateEventAsync(payload);
        if (payload.status === 'approved') {
          onApprove?.(eventId);
        }
        onClose(true);
      }}
    />
  );
};

const InstagramUser: FC<{
  tagId: string;
  instagramAccount: any;
  events: any;
  onAddEvent: (postId: string, index: number, imgUrl: string) => void;
}> = ({ tagId, instagramAccount: initialInstagramAccount, events, onAddEvent }) => {
  const [instagramAccount, setInstagramAccount] = useState(initialInstagramAccount);
  const [isExpanded, setIsExpanded] = useState(false);

  const [isRefetching, setIsRefetching] = useState(false);
  const handleRefetch = async () => {
    setIsRefetching(true);
    try {
      const { results } = await API.post<
        {
          results: any[];
        },
        {}
      >(`/v1/series/${tagId}/instagram-handle-posts/refetch`, {
        username: instagramAccount.username,
      });

      setInstagramAccount(results[0]);
    } catch (e) {
      Sentry.captureException(e);
      alert('Failed to refetch posts');
    } finally {
      setIsRefetching(false);
    }
  };

  return (
    <div key={instagramAccount.username}>
      <TitleToolbar
        text={instagramAccount.username}
        size="sm"
        action={
          <IconButton variant="flat" color="neutral" onClick={() => setIsExpanded(!isExpanded)}>
            <FontAwesomeIcon name={isExpanded ? 'chevron-up' : 'chevron-down'} />
          </IconButton>
        }
      />
      {isExpanded && (
        <div>
          <div className="mb-2">
            <Button
              disabled={isRefetching}
              variant="smooth"
              color="neutral"
              roundness="rounded"
              onClick={handleRefetch}
            >
              Refetch Posts
            </Button>
          </div>
          {instagramAccount.latestPosts?.map((post) => (
            <div key={post.id} className="mb-2">
              {post.images?.length ? (
                <HorizontalScroll
                  items={post.images.map((url, index) => (
                    <CarouselItem
                      key={url}
                      username={instagramAccount.username}
                      postId={post.id}
                      index={index}
                      events={events[post.id]?.[index]}
                      onAddEvent={(imgUrl) => onAddEvent(post.id, index, imgUrl)}
                    />
                  ))}
                />
              ) : (
                <CarouselItem
                  username={instagramAccount.username}
                  postId={post.id}
                  index={0}
                  events={events[post.id]?.[0]}
                  onAddEvent={(imgUrl) => onAddEvent(post.id, 0, imgUrl)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

enum ExistingEventMode {
  Default = 'default',
  Categorize = 'categorize',
  Referrer = 'referrer',
  Entertainers = 'entertainers',
}

const EXISTING_EVENT_MODE_OPTIONS: Record<ExistingEventMode, string> = {
  [ExistingEventMode.Default]: 'Default',
  [ExistingEventMode.Categorize]: 'Categorize',
  [ExistingEventMode.Referrer]: 'Referrer',
  [ExistingEventMode.Entertainers]: 'Entertainers',
};

enum PendingEventFilter {
  Default = 'default',
  HighConfidence = 'highConfidence',
}

const PENDING_EVENT_FILTER_OPTIONS: Record<PendingEventFilter, string> = {
  [PendingEventFilter.Default]: 'Default',
  [PendingEventFilter.HighConfidence]: 'High Confidence',
};

const PendingEventsSection: FC<{
  tagId: string;
  city: { _id: string; name: string };
  cities: { _id: string; name: string }[];
  onApprove: (event: FeedEvent) => void;
}> = ({ tagId, city, cities, onApprove }) => {
  const {
    pendingEventsAreReady,
    pendingEvents: unsortedPendingEvents = [],
    refetchPendingEvents,
  } = useSeriesPendingEvents(tagId, { enabled: Boolean(tagId) });

  const [reviewedPendingEvents, setReviewedPendingEvents] = useState<Record<string, boolean>>({});
  const [updatedEvents, setUpdatedEvents] = useState<
    Record<string, Partial<SubmitEventProperties>>
  >({});
  const [editEventId, setEditEventId] = useState<string>();
  const [pendingEventFilter, setPendingEventFilter] = useState<PendingEventFilter>(
    PendingEventFilter.HighConfidence,
  );

  const handleMergeSave = (eventId: string, updatedProperties: Partial<SubmitEventProperties>) => {
    setUpdatedEvents((prevValue) => ({
      ...prevValue,
      [eventId]: updatedProperties,
    }));
  };

  const mergedPendingEvents = useMemo(() => {
    return sortBy(
      unsortedPendingEvents.map(
        (event) => ({ ...event, ...(updatedEvents[event._id] ?? {}) }) as FeedEvent,
      ),
      'startDate',
    );
  }, [unsortedPendingEvents, updatedEvents]);

  const filteredPendingEvents = useMemo(() => {
    if (pendingEventFilter !== PendingEventFilter.HighConfidence) {
      return mergedPendingEvents;
    }

    return mergedPendingEvents.filter((event) => event.confidenceLevel === 'high');
  }, [mergedPendingEvents, pendingEventFilter]);

  const [didRefetchPendingEvents, setDidRefetchPendingEvents] = useState(false);
  const handleRefetchPendingEvents = async () => {
    setDidRefetchPendingEvents(true);
    await refetchPendingEvents();
    setTimeout(() => {
      setDidRefetchPendingEvents(false);
    });
  };

  if (!pendingEventsAreReady) {
    return null;
  }

  return (
    <>
      {!!mergedPendingEvents.length && (
        <>
          <TitleToolbar
            text="Pending Events"
            size="md"
            action={
              <Button
                size="sm"
                onClick={handleRefetchPendingEvents}
                variant="smooth"
                color="neutral"
                disabled={didRefetchPendingEvents}
              >
                {didRefetchPendingEvents ? 'Refetched!' : 'Refetch'}
              </Button>
            }
          />
          <div>
            <Select
              value={pendingEventFilter}
              options={Object.entries(PENDING_EVENT_FILTER_OPTIONS).map(([value, label]) => ({
                value,
                label,
              }))}
              onChange={(e) => setPendingEventFilter(e.target.value as PendingEventFilter)}
            />
          </div>
          <div className="my-2">
            {filteredPendingEvents.length ? (
              <HorizontalScroll
                items={filteredPendingEvents.map((event) => (
                  <div className="w-100 h-100 d-flex" key={event._id}>
                    <PendingEventCard
                      event={event}
                      tagId={tagId}
                      cityId={city._id}
                      onEdit={() => setEditEventId(event._id)}
                      onActionCallback={() =>
                        setReviewedPendingEvents((prevValue) => ({
                          ...prevValue,
                          [event._id]: true,
                        }))
                      }
                      onMergeSave={handleMergeSave}
                      isReviewed={reviewedPendingEvents[event._id]}
                    />
                  </div>
                ))}
              />
            ) : (
              <EmptyState
                title="No High Confidence Events"
                text="Switch the filter to Default to see all pending events."
              />
            )}
          </div>
        </>
      )}
      <QueueEventProcessingButton tagId={tagId} />
      <SeriesAdminEditEventPopup
        cityOptions={cities.map(({ _id, name }) => ({ value: _id, label: name }))}
        eventId={editEventId}
        tagId={tagId}
        onApprove={(approvedEventId) => {
          const approvedEvent = mergedPendingEvents.find(({ _id }) => _id === approvedEventId);
          if (approvedEvent) {
            onApprove(approvedEvent);
          }
        }}
        onClose={(didSave) => {
          if (didSave) {
            setReviewedPendingEvents((prevValue) => ({
              ...prevValue,
              [editEventId]: true,
            }));
          }

          setEditEventId(undefined);
        }}
      />
    </>
  );
};

export const SeriesAdmin: FC<{
  tagSlug: string;
  tagId: string;
  referrerOptions: { value: string; label: string }[];
  city: { _id: string; name: string };
  cities: { _id: string; name: string }[];
}> = ({ tagSlug, tagId, referrerOptions, city, cities }) => {
  const { track } = useAnalytics();
  const { seriesInstagramHandleLatestPosts, refetchSeriesInstagramHandleLatestPosts } =
    useSeriesInstagramHandleLatestPostsQuery(tagId);
  const [events, setEvents] = useState<Record<string, Record<number, SubmitEventProperties[]>>>({});
  const [editEventId, setEditEventId] = useState<string>();
  const { series } = useSeriesByTagSlug(tagSlug);

  const { eventsAreReady: seriesEventsAreReady, events: existingSeriesEvents = [] } =
    useUnauthorizedEventsQuery(
      {
        cityId: city?._id,
        startDate: dayjs().format('YYYY-MM-DD'),
        rsvpData: 'ignore',
        forceSupply: 'true',
        tagIds: [tagId],
      },
      Boolean(tagId),
    );

  const {
    eventsAreReady,
    events: existingEvents = [],
    refetchEvents: refetchExistingEvents,
  } = useUnauthorizedEventsQuery(
    {
      cityId: city?._id,
      startDate: dayjs().format('YYYY-MM-DD'),
      rsvpData: 'ignore',
      forceSupply: 'true',
    },
    Boolean(tagId),
  );

  const [approvedPendingEvents, setApprovedPendingEvents] = useState<Record<string, FeedEvent>>({});
  const handlePendingEventApproved = (event: FeedEvent) => {
    setApprovedPendingEvents((prevValue) => ({
      ...prevValue,
      [event._id]: event,
    }));
  };

  const approvedAndExistingEvents = useMemo(() => {
    return sortBy([...existingEvents, ...Object.values(approvedPendingEvents)], 'startDate');
  }, [existingEvents, approvedPendingEvents]);

  const [base64Image, setBase64Image] = useState<string | ArrayBuffer>();
  const postRef = useRef<{ postId: string; index: number }>(undefined);
  const handleAddEvent = async (postId: string, postIndex: number, imgUrl: string) => {
    const updatedBase64Image = await imageUrlToBase64(imgUrl);
    postRef.current = { postId, index: postIndex };
    setBase64Image(updatedBase64Image);
  };

  const [isRefetching, setIsRefetching] = useState(false);
  const [refetchedPosts, setRefetchedPosts] = useState<any[]>();
  const handleRefetch = async () => {
    setIsRefetching(true);
    try {
      const { results } = await API.post<
        {
          results: any[];
        },
        {}
      >(`/v1/series/${tagId}/instagram-handle-posts/refetch`, {});
      setRefetchedPosts(results);
    } catch (e) {
      Sentry.captureException(e);
      alert('Failed to refetch posts');
    } finally {
      setIsRefetching(false);
    }
  };

  const { isCreatingEvent, createEventAsync } = useCreateEventMutation();
  const handleSubmit = async () => {
    track('Submit Admin Series Events');

    const failedEvents: Record<string, Record<number, SubmitEventProperties[]>> = {};

    try {
      await Promise.all(
        Object.entries(events).flatMap(([postId, indexEvents]) =>
          Object.entries(indexEvents).flatMap(([indexString, indexEvent]) =>
            indexEvent.map(async (event) => {
              try {
                await createEventAsync({
                  ...event,
                  entertainerIds: event?.entertainerIds ?? [],
                  tagIds: [tagId],
                });
              } catch (e) {
                Sentry.captureException(e);
                const index = Number(indexString);
                failedEvents[postId][index] = [...(failedEvents?.[postId]?.[index] || []), event];
              }
            }),
          ),
        ),
      );

      setEvents(failedEvents);
    } catch (e) {
      Sentry.captureException(e);
      alert('Failed to save events');
    }
  };

  const [manuallyFetchedAccounts, setManuallyFetchedAccounts] = useState<any[]>([]);
  const [manualUsername, setManualUsername] = useState('');
  const [isSubmittingManualUsername, setIsSubmittingManualUsername] = useState(false);
  const handleManualUsernameRefetch = async () => {
    setIsSubmittingManualUsername(true);
    try {
      const { results } = await API.post<
        {
          results: any[];
        },
        {}
      >(`/v1/series/${tagId}/instagram-handle-posts/refetch`, {
        username: manualUsername,
      });

      setManuallyFetchedAccounts((prev) => [...prev, ...results]);
    } catch (e) {
      Sentry.captureException(e);
      alert('Failed to refetch posts');
    } finally {
      setIsSubmittingManualUsername(false);
    }
  };

  const instagramAccounts = useMemo(() => {
    const defaultAccounts = refetchedPosts ?? seriesInstagramHandleLatestPosts?.results ?? [];

    return [...manuallyFetchedAccounts, ...defaultAccounts];
  }, [manuallyFetchedAccounts, refetchedPosts, seriesInstagramHandleLatestPosts]);

  const [existingEventMode, setExistingEventMode] = useState<ExistingEventMode>(
    ExistingEventMode.Default,
  );

  return (
    <div>
      <div className="mb-3">
        <TitleToolbar
          text={
            <div className="d-flex gap-2 align-items-center">
              <span>Existing Events</span>
              <IconButton
                variant="smooth"
                color={'neutral'}
                onClick={() => refetchExistingEvents()}
                size="md"
              >
                <LineAwesomeIcon name="redo-alt" />
              </IconButton>
            </div>
          }
          size="md"
          action={
            <>
              <Select
                value={existingEventMode}
                options={Object.entries(EXISTING_EVENT_MODE_OPTIONS).map(([value, label]) => ({
                  value,
                  label,
                }))}
                onChange={(e) => {
                  setExistingEventMode(e.target.value as ExistingEventMode);
                }}
              ></Select>
            </>
          }
        />
        <div className="mt-2">
          {existingEventMode === 'default' && (
            <HorizontalScroll
              items={approvedAndExistingEvents.map((event) => (
                <div className="w-100 h-100 d-flex" key={event._id}>
                  <ExistingEventCard
                    event={event}
                    tagId={tagId}
                    onEdit={() => setEditEventId(event._id)}
                    onEditRecurringParentEvent={setEditEventId}
                  />
                </div>
              ))}
            />
          )}

          {existingEventMode === 'categorize' && (
            <CategorizeExistingEventsList events={existingSeriesEvents} />
          )}

          {existingEventMode === 'referrer' && (
            <ExistingEventsReferrerList
              events={existingSeriesEvents}
              referrerOptions={referrerOptions}
            />
          )}

          {existingEventMode === 'entertainers' && (
            <ExistingEventsEntertainersList tagId={tagId} events={existingSeriesEvents} />
          )}
        </div>
      </div>

      <PendingEventsSection
        tagId={tagId}
        city={city}
        cities={cities}
        onApprove={handlePendingEventApproved}
      />

      <SeriesVenuesSection tagId={tagId} />

      <SeriesProducersSection tagId={tagId} />

      <TitleToolbar text="New Events" size="md" />
      <div className="d-flex gap-2">
        <Button roundness="rounded" disabled={isRefetching} onClick={handleRefetch}>
          {isRefetching ? 'Refetching...' : 'Refetch'}
        </Button>
        <Button roundness="rounded" onClick={() => refetchSeriesInstagramHandleLatestPosts()}>
          Refresh
        </Button>
      </div>
      <div className="mt-2">
        <Button roundness="rounded" disabled={isCreatingEvent} onClick={handleSubmit}>
          Save Events
        </Button>
      </div>
      <div className="mt-2 d-flex align-items-center gap-2">
        <div className="w-100">
          <Input value={manualUsername} onChange={(e) => setManualUsername(e.target.value)} />
        </div>
        <div>
          <Button
            roundness="rounded"
            variant="smooth"
            color="neutral"
            disabled={isSubmittingManualUsername}
            onClick={handleManualUsernameRefetch}
            style={{ height: 44 }}
          >
            Fetch
          </Button>
        </div>
      </div>
      <div className="mt-3">
        {instagramAccounts?.map((instagramAccount) => (
          <InstagramUser
            key={instagramAccount.username}
            tagId={tagId}
            instagramAccount={instagramAccount}
            events={events}
            onAddEvent={handleAddEvent}
          />
        ))}
      </div>
      <CreateEditEventPopup
        isVisible={!!base64Image}
        onClose={() => {
          setBase64Image(undefined);
        }}
        defaultValues={{
          eventImage: base64Image?.toString(),
        }}
        cityOptions={cities.map(({ _id, name }) => ({ value: _id, label: name }))}
        onSubmit={async (payload) => {
          setBase64Image(undefined);

          if (postRef.current) {
            const { postId, index } = postRef.current;
            setEvents((prev) => ({
              ...prev,
              [postId]: {
                ...(prev?.[postId] || {}),
                [index]: [...(prev?.[postId]?.[index] || []), payload],
              },
            }));
          }

          postRef.current = undefined;
        }}
      />
      <SeriesAdminEditEventPopup
        cityOptions={cities.map(({ _id, name }) => ({ value: _id, label: name }))}
        eventId={editEventId}
        tagId={tagId}
        onApprove={() => refetchExistingEvents()}
        onClose={() => {
          setEditEventId(undefined);
        }}
      />
    </div>
  );
};
